import type {ComponentStageData, ComponentStageHeuristicDefinition} from '../../../types'
import {getParent} from '../../common'
import {getSourceLayoutPointer, getStructurePointer, getTargetLayoutPointer} from '../../../conversionDal/utils/pointer'
import {updateComponentVerticalMargins} from '../common'
import {verticalSpacingHeuristics} from './heuristics'
import {TOP_BOTTOM_RELATION, TOP_PADDING} from '../constants'

const isInHorizontalStack = (componentStageData: ComponentStageData): boolean => {
    const {conversionDal, componentPointer} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return false
    const targetLayout = conversionDal.get(getTargetLayoutPointer(parentPointer))
    return targetLayout?.containerLayout?.direction === 'row'
}

const isFirstChildInStack = (componentStageData: ComponentStageData): boolean => {
    const {conversionDal, componentPointer} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return false
    const parentStructure = conversionDal.get(parentPointer)
    return parentStructure.components!.indexOf(componentPointer.id) === 0
}

const isLastChildInStack = (componentStageData: ComponentStageData): boolean => {
    const {conversionDal, componentPointer} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return false
    const parentStructure = conversionDal.get(parentPointer)
    const components = parentStructure.components!
    return components.indexOf(componentPointer.id) === components.length - 1
}

const getTopMarginFromPreviousSibling = (componentStageData: ComponentStageData): number => {
    const {conversionDal, componentPointer} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)!
    const parentStructure = conversionDal.get(parentPointer)
    const compIndex = parentStructure.components!.indexOf(componentPointer.id)

    if (compIndex <= 0) return TOP_PADDING.ROOT

    const previousSiblingId = parentStructure.components![compIndex - 1]
    const previousSibling = conversionDal.get(getStructurePointer({id: previousSiblingId}))
    const currentSemanticType = conversionDal.get(componentPointer).semanticType

    return TOP_BOTTOM_RELATION[currentSemanticType]?.[previousSibling.semanticType] ?? TOP_PADDING.ROOT
}

const updateHorizontalStackItemVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const top = itemLayout?.margins?.left?.top ?? 0
    const bottom = itemLayout?.margins?.right?.bottom ?? 0
    updateComponentVerticalMargins(componentStageData, top, bottom)
}

export const verticalSpacingHeuristicsStack: ComponentStageHeuristicDefinition[] = [
    {
        predicate: isInHorizontalStack,
        apply: (componentStageData: ComponentStageData) => {
            updateHorizontalStackItemVerticalMargins(componentStageData)
        },
        heuristicCode: 'HORIZONTAL STACK ITEM VERTICAL MARGINS'
    },
    {
        predicate: (componentStageData: ComponentStageData) =>
            isFirstChildInStack(componentStageData) && isLastChildInStack(componentStageData),
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, 0, 0)
        },
        heuristicCode: 'ONLY CHILD IN STACK ZERO MARGINS'
    },
    {
        predicate: isFirstChildInStack,
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, 0)
        },
        heuristicCode: 'FIRST CHILD IN STACK ZERO TOP MARGIN'
    },
    {
        predicate: isLastChildInStack,
        apply: (componentStageData: ComponentStageData) => {
            const topMargin = getTopMarginFromPreviousSibling(componentStageData)
            updateComponentVerticalMargins(componentStageData, topMargin, 0)
        },
        heuristicCode: 'LAST CHILD IN STACK ZERO BOTTOM MARGIN'
    },
    ...verticalSpacingHeuristics
]
